import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import { format } from 'date-fns'
import { Box, Flex, Text, TextBox, H1, Card, Media, Circle } from '@revolut/ui-kit'
import * as Icons from '@revolut/icons'

import { setActiveScreenAction, setErrorAction } from 'reducers/generalReducer'
import type { GeneralState } from 'reducers/generalReducer'
import type { AppState } from 'store'
import type { AccessToken, Error, Pocket, Rate, PaymentConsent } from 'types/common'
import { fetchPocketsAction } from 'thunks'
import type { PocketsState } from 'reducers/pocketsReducer'
import { FetchStates, OBA_FREQUENCIES, OBA_SCHEMES, Screens } from 'constants/common'
import { formatByScheme, formatMoney } from 'helpers/common'
import { Loader } from 'components/Loader'
import { CardSlide } from 'components/CardSlide'
import { AccountSelect } from 'components/AccountSelect'
import { DetailsSection, DetailsSectionType } from 'components/PaymentDetailsSection'
import { Notification } from 'components/Notification'
import { AccountDisplay } from 'components/AccountDisplay/AccountDisplay'
import api, { ApplicationData } from 'services/api'
import { ApmAvatar } from 'components/ApmAvatar'
import { Divider } from '../../components/Divider'

type Props = {
  applicationData: ApplicationData
  general: GeneralState<PaymentConsent>['data']
  handleSubmit: () => void
  onBack: () => void
  onClose: () => void
  pockets: PocketsState
  setFieldValue: (field: string, value?: any) => void
  fetchPockets: (accessToken: AccessToken) => void
  setError: (errorData: Error) => void
  setActiveScreen: (screen: Screens) => void
}

const PaymentConfirmationComponent: React.FC<Props> = ({
  applicationData,
  general: {
    consent: {
      details: {
        currencyOfTransfer,
        pocketId,
        currency,
        amountValue,
        creditor,
        merchant,
        frequency,
        firstPaymentDateTime,
        requestedExecutionDateTime,
        numberOfPayees,
        apm,
      },
    },
    accessToken,
  },
  pockets,
  fetchPockets,
  setError,
  setFieldValue,
  setActiveScreen,
  handleSubmit,
  onBack,
  onClose,
}) => {
  const [accountToCharge, setAccountToCharge] = useState<Pocket | null>(null)
  const [rate, setRate] = useState<Rate | null>(null)
  const isInternational = !!currencyOfTransfer
  const isRecurring = !!frequency
  const isScheduled = !!requestedExecutionDateTime

  if (numberOfPayees !== undefined) {
    setError({
      message: 'Payment type is not supported',
      description:
        'Bulk payments are not currently supported in Revolut personal account',
    })
    setActiveScreen(Screens.ERROR)
  }

  useEffect(() => {
    fetchPockets(accessToken)
  }, [accessToken, fetchPockets])

  useEffect(() => {
    if (accountToCharge) {
      setFieldValue('pocket', accountToCharge.id)
    }
  }, [accountToCharge, setFieldValue])

  useEffect(() => {
    if (pockets.fetchState === FetchStates.SUCCESS) {
      if (pocketId) {
        const foundPocket = pockets.data.find((pocket) => pocket.id === pocketId)
        if (!foundPocket) {
          setError({
            message: 'Account not found',
            description: 'Please double check your account number and sort code.',
          })
          setActiveScreen(Screens.ERROR)
        }
        setAccountToCharge(foundPocket || null)
      } else {
        setAccountToCharge(
          pockets.data.find((pocket) => pocket.currency === currency) || null,
        )
      }
    }
  }, [
    currency,
    isInternational,
    pocketId,
    pockets.data,
    pockets.fetchState,
    setActiveScreen,
    setError,
  ])

  const onAccountSelectedCallback = useCallback(
    (accountId: string) => {
      setAccountToCharge(pockets.data.find((pocket) => pocket.id === accountId) || null)
    },
    [pockets.data],
  )

  useEffect(() => {
    if (
      accountToCharge &&
      currencyOfTransfer &&
      accountToCharge.currency !== currencyOfTransfer
    ) {
      api
        .fetchRate(
          {
            from: currencyOfTransfer,
            to: accountToCharge.currency,
            amount: amountValue,
          },
          accessToken,
        )
        .then((response: Rate) => setRate(response))
    } else {
      setRate(null)
    }
  }, [accessToken, accountToCharge, amountValue, currencyOfTransfer])

  const pocketsToSelect: Pocket[] = useMemo(() => {
    if (pockets.fetchState !== FetchStates.SUCCESS || !pockets.data) {
      return []
    }
    if (pocketId) {
      const foundPocket = pockets.data.find((pocket) => pocket.id === pocketId)
      return foundPocket ? [foundPocket] : []
    }
    if (isInternational) {
      return pockets.data
    }
    return pockets.data.filter((pocket) => pocket.currency === currency)
  }, [currency, isInternational, pocketId, pockets])

  const hasInsufficientFunds = useCallback(() => {
    if (!accountToCharge) {
      return false
    }
    if (isInternational && !rate) {
      return false
    }
    const amount = isInternational ? (rate as Rate).to.amount : amountValue
    return accountToCharge.balance < amount
  }, [accountToCharge, amountValue, isInternational, rate])

  if (pockets.fetchState === FetchStates.PENDING) {
    return (
      <Flex alignItems='center' minHeight='100vh'>
        <Loader />
      </Flex>
    )
  }

  const formattedAmount = formatMoney({
    currency,
    amount: amountValue,
  })

  let frequencyString = ''

  if (frequency) {
    if (frequency.startsWith(OBA_FREQUENCIES.EvryDay)) {
      frequencyString = 'Daily'
    } else if (frequency.startsWith(OBA_FREQUENCIES.IntrvlWkDay)) {
      frequencyString = 'Weekly'
    } else if (frequency.startsWith(OBA_FREQUENCIES.IntrvlMnthDay)) {
      frequencyString = 'Monthly'
    } else {
      frequencyString = 'Unknown'
    }
  }

  const paymentDetailsSection: DetailsSectionType = {
    title: 'Payment information',
    sectionRows: [
      ...(creditor.accountNumber
        ? [
            {
              description: 'Account number:',
              value: creditor.accountNumber,
            },
          ]
        : []),
      ...(creditor.sortCode
        ? [
            {
              description: 'Sort code:',
              value: formatByScheme({
                scheme: OBA_SCHEMES.SortCode,
                identification: creditor.sortCode,
              }),
            },
          ]
        : []),
      ...(creditor.iban
        ? [
            {
              description: 'IBAN:',
              value: formatByScheme({
                scheme: OBA_SCHEMES.IBAN,
                identification: creditor.iban,
              }),
            },
          ]
        : []),
      ...(apm
        ? [
            {
              description: 'Description:',
              value: apm.description,
            },
          ]
        : []),
      ...(frequency && firstPaymentDateTime
        ? [
            {
              description: 'First payment due:',
              value: format(firstPaymentDateTime, 'dd.MM.yyyy'),
            },
            {
              description: 'Frequency:',
              value: frequencyString,
            },
            {
              description: 'Last date:',
              value: 'Until further notice',
            },
          ]
        : []),
      ...(requestedExecutionDateTime
        ? [
            {
              description: 'Payment date:',
              value: format(requestedExecutionDateTime, 'dd.MM.yyyy'),
            },
          ]
        : []),
      ...(frequency || requestedExecutionDateTime
        ? [
            {
              description: (
                <Text variant='secondary' color='grey-35'>
                  Payments in this schedule that fall on weekends and bank holidays will
                  be made on the following business day.
                </Text>
              ),
            },
          ]
        : []),
    ],
  }

  const exchangeSection: DetailsSectionType = {
    title: 'Exchange rate and charges',
    sectionRows: [
      ...(rate && accountToCharge && currencyOfTransfer
        ? [
            {
              description: `Amount (${currencyOfTransfer}):`,
              value: formatMoney({
                amount: amountValue,
                currency: currencyOfTransfer,
              }),
            },
            {
              description: `Amount (${accountToCharge.currency}):`,
              value: formatMoney({
                amount: rate.to.amount,
                currency: accountToCharge.currency,
              }),
            },
            {
              description: `Exchange rate:`,
              value: `1 ${currencyOfTransfer} = ${rate.rate} ${accountToCharge.currency}`,
            },
            {
              description: (
                <Text variant='secondary' color='grey-35'>
                  The exchange rate shown here may be different to the actual rate used
                  for the payment order.
                </Text>
              ),
            },
          ]
        : []),
      {
        description: (
          <Text variant='secondary' color='grey-35'>
            The corrensponding bank may charge additional fees. Please contact the
            beneficiary&apos;s bank for more details.
          </Text>
        ),
      },
    ],
  }

  if (!pocketsToSelect.length) {
    setError({
      message: 'Account not found',
      description: 'Please double check your account number and sort code.',
    })
    setActiveScreen(Screens.ERROR)
  }

  return (
    <>
      <CardSlide
        onSubmit={handleSubmit}
        header={
          <>
            <H1>
              {isScheduled ? 'Scheduled payment' : ''}
              {isRecurring ? 'Recurring payment' : ''}
              {!isRecurring && !isScheduled ? 'Payment' : ''} request
            </H1>
            {apm && <ApmAvatar apmProvider={apm.provider} />}
            <TextBox color='grey-50' mt='0.75rem' mb='2rem'>
              {apm ? (
                <Text>Revolut</Text>
              ) : (
                <Text color='black' fontWeight={500}>
                  {applicationData.name}
                </Text>
              )}{' '}
              will set up a{isScheduled ? ' scheduled' : ''}
              {isRecurring ? ' recurring' : ''} payment from your account and execute it
              after you authorise it.
            </TextBox>
            <Card py='1.25rem' pl='1.75rem' pr='2rem' variant='ghost'>
              <Flex>
                <Circle bg='primary' size={56}>
                  <Icons.Bank color='white' />
                </Circle>
                <Flex ml='1rem' flexDirection='column' flex={1}>
                  <Media my='.25rem'>
                    <Media.Content>
                      <Text color='black' fontWeight={500}>
                        {merchant}
                      </Text>
                    </Media.Content>
                    <Media.Side>
                      <Text color='black'>{formattedAmount}</Text>
                    </Media.Side>
                  </Media>
                  <TextBox mb='.25rem' variant='secondary' color='grey-50'>
                    {isScheduled &&
                      `Scheduled for ${format(
                        requestedExecutionDateTime!,
                        'dd.MM.yyyy',
                      )}`}
                    {!isScheduled && `${isRecurring ? 'Recurring' : 'Single'} payment`}
                  </TextBox>
                </Flex>
              </Flex>
            </Card>
          </>
        }
        buttonText='Authorise'
        onBack={onBack}
        onClose={onClose}
      >
        <Flex flexDirection='column' alignItems='center'>
          <Flex width='100%' flexDirection='column'>
            <TextBox color='black' fontWeight={500} mb='1.5rem'>
              Account to charge
            </TextBox>
            <Box mb='.5rem'>
              {pocketsToSelect.length > 1 ? (
                <AccountSelect
                  onChange={onAccountSelectedCallback}
                  accounts={pocketsToSelect}
                  value={accountToCharge && accountToCharge.id}
                />
              ) : (
                <AccountDisplay
                  account={pocketsToSelect[0]}
                  showFlag={false}
                  labelProps={{ color: 'grey-50' }}
                  balanceProps={{ color: 'black' }}
                />
              )}
            </Box>
            {hasInsufficientFunds() && (
              <Notification
                my='.5rem'
                message=' Please be aware that this payment may not go through due to insufficient funds.'
                level='primary'
              />
            )}
            <Divider
              borderColor='grey-100'
              mx={{ all: '-1rem', md: '-2rem' }}
              my='1.5rem'
            />
            <DetailsSection {...paymentDetailsSection} mb='.5rem' />
            {isInternational && (
              <>
                <Divider
                  borderColor='grey-100'
                  mx={{ all: '-1rem', md: '-2rem' }}
                  my='1.5rem'
                />
                <DetailsSection {...exchangeSection} mb='.5rem' />
              </>
            )}
          </Flex>
        </Flex>
      </CardSlide>
    </>
  )
}

const mapStateToProps = ({ pockets, general }: AppState) => ({
  general: general.data,
  pockets,
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  fetchPockets: (accessToken: AccessToken) => dispatch(fetchPocketsAction(accessToken)),
  setError: ({ message, description }: Error) =>
    dispatch(setErrorAction({ message, description })),
  setActiveScreen: (screen: Screens) => dispatch(setActiveScreenAction(screen)),
})

const ConnectedPaymentConfirmation = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PaymentConfirmationComponent)

export { ConnectedPaymentConfirmation as PaymentConfirmation }
